import MuiTable from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import type { Definition, Organization, SortedHeadCells } from '../../types'
import TableHeader from './TableHeader'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import type { Order } from './types'
import { getComparator, stableSort } from './utils'
import { Box, InputAdornment, Stack, TextField } from '@mui/material'
import capitalize from 'lodash/capitalize'
import NavBar from '../NavBar/NavBar'
import { TableToggle, TableToggleMobile } from '../MapTableToggle'
import checkMark from './images/checkmark.svg'
//import csvDownload from './images/csv_download.svg'
import { CustomSelect } from './components'
import styles from './Table.module.scss'
import {
  MobileScreen6,
  MobileScreen7,
} from '../ExplainerStepsContainer/components'
import closeIcon from '../Map/images/close-icon-orange-mobile.svg'
import { STRATEGY_PARENT_OPTIONS, TARGET_PARENT_OPTIONS } from '../../constants'
import { TableToggleSwitch } from '../MapTableToggle/TableToggleSwitch'

type TableProps = {
  data: Organization[]
  definitions: Definition[]
  onOrganizationClick: (
    organization: Organization | null,
    organizations: Organization[]
  ) => void
  onExplainerStepChange: (step: number) => void
  explainerStep: number
}

function Table({
  data,
  definitions,
  onOrganizationClick,
  explainerStep,
  onExplainerStepChange,
}: TableProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [search, setSearch] = useState<string>('')
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof SortedHeadCells>('org_name')
  const downloadRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const [scrolledRight, setScrolledRight] = useState<boolean>(false)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof SortedHeadCells
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
    onExplainerStepChange(0)
  }, [])

  const visibleRows = useMemo(
    () =>
      stableSort<SortedHeadCells>(
        data.filter((row) => {
          const lowerCaseSearch = search
            .toLowerCase()
            .replace(/[!"#%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/g, '')
          return (
            (row.org_name || '')
              .toLowerCase()
              .replace(/[!"#%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/g, '')
              .includes(lowerCaseSearch) ||
            (row.org_about || '').toLowerCase().includes(lowerCaseSearch) ||
            (row.city || '').toLowerCase().includes(lowerCaseSearch) ||
            (row.state || '').toLowerCase().includes(lowerCaseSearch) ||
            (row.geo_scope || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.focus || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.target || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.strategy || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.stage || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.repair || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            (row.initiative_type || []).some((d) =>
              d.toLowerCase().includes(lowerCaseSearch)
            ) ||
            row.budget_amount?.toLowerCase().includes(lowerCaseSearch) ||
            row.budget_date?.toLowerCase().includes(lowerCaseSearch) ||
            row.value_amount?.toLowerCase().includes(lowerCaseSearch) ||
            row.value_date?.toLowerCase().includes(lowerCaseSearch)
          )
        }),
        getComparator(order, orderBy)
      ),
    [data, order, orderBy, search]
  )

  const renderExplainerStepScreen = () => {
    switch (explainerStep) {
      case 1:
        return <MobileScreen6 anchorRef={searchRef} />
      case 2:
        return <MobileScreen7 anchorRef={downloadRef} />
      default:
        return null
    }
  }

  return (
    <section id="table" className="min-h-screen h-full bg-white relative">
      {explainerStep > 0 && (
        <div
          className="absolute min-h-screen h-full w-full z-[1000] bg-[#222222] bg-opacity-[.78]"
          onClick={() => {
            explainerStep === 2
              ? onExplainerStepChange(0)
              : onExplainerStepChange(explainerStep + 1)
          }}
        >
          <img
            src={closeIcon}
            alt="letter x"
            className="absolute top-0 right-0 p-8 z-[1000]"
            role="button"
            onClick={(evt) => {
              evt.stopPropagation()
              onExplainerStepChange(0)
            }}
          />
          {renderExplainerStepScreen()}
        </div>
      )}
      <div className="w-full px-8 gap-2 flex flex-col">
        <NavBar theme={'dark'} />

        <div className="flex flex-grow flex-col w-full justify-center">
          <div className="w-full">
            <div className="mr-5 flex flex-col lg:items-center lg:flex-row lg:justify-between w-full mb-4 lg:mb-6">
              <p className="font-decimal text-ink text-[26px] lg:whitespace-nowrap mb-3 lg:mb-0">
                Mapping the U.S.{' '}
                <span className="font-bold">Reparations Movement</span>
              </p>
              <TableToggleSwitch />
            </div>
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <Stack className="flex-col mb-3 lg:mb-0">
                <Stack direction="row" className="mb-2">
                  <div className="rounded-full bg-green flex items-center justify-center font-bold text-white text-[16px] min-w-[22px] h-[22px] mr-2">
                    1
                  </div>
                  <div>
                    Use the search box below to search by Keyword, or filter by
                    Geographic Scope, Liable Institution, or Focus Area
                  </div>
                </Stack>
                <Stack direction="row" className="mb-2">
                  <div className="rounded-full bg-green flex items-center justify-center font-bold text-white text-[16px] min-w-[22px] h-[22px] mr-2">
                    2
                  </div>
                  <div>
                    Click on a column header to sort by Name, City, State, Scope
                    or Status as Initiative
                  </div>
                </Stack>
                <Stack direction="row" className="mb-2">
                  <div className="rounded-full bg-green flex items-center justify-center font-bold text-white text-[16px] min-w-[22px] h-[22px] mr-2">
                    3
                  </div>
                  <div>Click on an Organization Name to see more details</div>
                </Stack>
              </Stack>
              <Box className="border border-ink h-max max-w-[400px] p-5 lg:ml-8">
                If you are interested in additional information or exploring
                this data further, please contact us at{' '}
                <a
                  className="text-green font-semibold"
                  href="mailto:map@firstrepair.org"
                >
                  map@firstrepair.org
                </a>
              </Box>
            </div>
            <div className="flex flex-col gap-6 px-0 sm:pr-8 py-8">
              <TextField
                ref={searchRef}
                className="max-w-[331px] sm:max-w-[664px]"
                placeholder="Search by Keyword..."
                variant="outlined"
                value={search}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    border: '1px solid #222222',
                    color: '#222222',
                    backgroundColor: 'transparent',
                  },
                  '& .Mui-focused': {
                    outline: '1px solid #222222',
                  },
                  '& .Mui-active': {
                    outline: '1px solid #222222',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!!search && (
                        <div
                          className="text-green text-[14px] underline font-decimal font-bold"
                          role="button"
                          onClick={() => setSearch('')}
                        >
                          Reset
                        </div>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                className={`${styles.horScrollWrap} ${
                  scrolledRight ? styles.scrolledRight : ''
                }`}
              >
                <TableContainer
                  ref={scrollRef}
                  onScroll={() => {
                    if (scrollRef?.current) {
                      const scrollLeft = scrollRef.current.scrollLeft + 1
                      const maxScrollWidth = scrollRef.current.clientWidth
                      const maxScrollAmt =
                        scrollRef.current.scrollWidth - maxScrollWidth
                      if (scrollLeft >= maxScrollAmt) {
                        setScrolledRight(true)
                      } else {
                        setScrolledRight(false)
                      }
                    }
                  }}
                  className="h-fit mb-[100px] sm:mb-0"
                >
                  <MuiTable
                    sx={{
                      minWidth: 650,
                    }}
                    size="small"
                  >
                    <TableHeader
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {(visibleRows as Organization[]).map((row) => {
                        return (
                          <TableRow tabIndex={-1} key={row.id}>
                            <TableCell>
                              <div
                                role="button"
                                className="text-[14px] text-black underline"
                                onClick={() => {
                                  onOrganizationClick(row, [])
                                }}
                              >
                                {row.org_name}
                              </div>
                            </TableCell>
                            <TableCell>{row.city}</TableCell>
                            <TableCell>{row.state}</TableCell>
                            <TableCell align="center">
                              <div className="flex items-center justify-center w-full">
                                {row.initCommTask ? (
                                  <img src={checkMark} alt="checkmark" />
                                ) : (
                                  ''
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Type"
                                options={(row.initiative_type || []).map(
                                  (t) => {
                                    return (
                                      definitions.find(
                                        (d) => d.id === `initiative_type:${t}`
                                      )?.display_label || ''
                                    )
                                  }
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              {(row.geo_scope || []).map(capitalize).join(', ')}
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Liable Institution"
                                options={(row.target || [])
                                  .filter((opt) => {
                                    return !TARGET_PARENT_OPTIONS.includes(
                                      `target:${opt}`
                                    )
                                  })
                                  .map((t) => {
                                    return (
                                      definitions.find(
                                        (d) => d.id === `target:${t}`
                                      )?.display_label || ''
                                    )
                                  })}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Focus Area"
                                options={(row.focus || []).map((t) => {
                                  return (
                                    definitions.find(
                                      (d) => d.id === `focus:${t}`
                                    )?.display_label || ''
                                  )
                                })}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Strategy"
                                options={(row.strategy || [])
                                  .filter((opt) => {
                                    return !STRATEGY_PARENT_OPTIONS.includes(
                                      `strategy:${opt}`
                                    )
                                  })
                                  .map((t) => {
                                    return (
                                      definitions.find(
                                        (d) => d.id === `strategy:${t}`
                                      )?.display_label || ''
                                    )
                                  })}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Stage"
                                options={(row.stage || []).map((t) => {
                                  return (
                                    definitions.find(
                                      (d) => d.id === `stage:${t}`
                                    )?.display_label || ''
                                  )
                                })}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomSelect
                                label="Form"
                                options={(row.repair || []).map((t) => {
                                  return (
                                    definitions.find(
                                      (d) => d.id === `repair:${t}`
                                    )?.display_label || ''
                                  )
                                })}
                              />
                            </TableCell>
                            <TableCell>{row.budget_amount}</TableCell>
                            <TableCell>{row.budget_date}</TableCell>
                            <TableCell>{row.value_amount}</TableCell>
                            <TableCell>{row.value_date}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </MuiTable>
                </TableContainer>
              </Box>
              {/* <div
                role="button"
                className="cursor-pointer hidden sm:block"
                onClick={() => downloadCSV(data)}
              >
                <img src={csvDownload} alt="Download as a CSV" />
              </div> */}
            </div>
          </div>
        </div>
        <div className="hidden w-full sm:block">
          <TableToggle />
        </div>
      </div>
      <div className="block sm:hidden">
        <TableToggleMobile
          data={data}
          onExplainerStepChange={onExplainerStepChange}
          downloadRef={downloadRef}
        />
      </div>
    </section>
  )
}

export default memo(Table)
